import { Css, Palette, Tooltip, useTestIds } from "@homebound/beam";
import { useMemo } from "react";
import { Icon } from "src/components";
import { generateEmptyDataset, LineChart } from "src/components/charts/LineChart";
import { getDateTicks, getNumberTicks } from "src/components/charts/utils";
import {
  BudgetOverviewTab_ProjectFragment,
  BudgetOverviewTab_SellableProjectFragment,
} from "src/routes/projects/budget/components/reports/BudgetOverviewTab.generated";
import { Temporal } from "temporal-polyfill";

type AverageCommittedAtStartWidgetProps = { project: BudgetOverviewTab_ProjectFragment };

export function AverageCommittedAtStartWidget({ project }: AverageCommittedAtStartWidgetProps) {
  const tid = useTestIds({});
  const chartData = useMemo(() => mapToLineChartData(project.sellableProjects), [project]);

  // The total average committed at start is effectively the last datapoint in the chart
  const averageCommittedAtStart = useMemo(() => Math.round(chartData.first?.data.last?.y ?? 0), [chartData]);

  // Calculate the number of ticks based on the data
  const { yTicks, xTicks } = useMemo(() => getChartTicks(chartData.flatMap((p) => p.data)), [chartData]);

  // Calculate the date range in months for the X-axis
  const dateRangeInMonths = useMemo(() => {
    const points = chartData.flatMap((p) => p.data);
    return points.length > 2 ? calendarMonthDiff(points.first!.x, points.last!.x) : 0;
  }, [chartData]);

  return (
    <div css={Css.w100.mw0.hPx(320).p3.bgWhite.ba.bcGray300.bw2.br8.$}>
      <div {...tid.percentageLabel} css={Css.tal.xl2.pb1.$}>
        {chartData.isEmpty ? "--%" : `${averageCommittedAtStart}%`}
      </div>
      <div css={Css.lg.gray700.pb5.df.$}>
        Average Committed at Start
        <div css={Css.mya.pl1.$}>
          <Tooltip title="The average percentage committed on POs at the start across all projects." placement="top">
            <Icon icon="infoCircle" color={Palette.Gray600} inc={2.3} />
          </Tooltip>
        </div>
      </div>
      <div css={Css.h("55%").$} {...tid.LineChart}>
        <LineChart
          dataset={chartData.isEmpty ? generateEmptyDataset(73, 100) : chartData}
          colors={[chartData.isEmpty ? Palette.Transparent : Palette.Blue400]}
          loadingColor={[Palette.Transparent]}
          disabled={chartData.isEmpty}
          emptyStateTooltipText={chartData.isEmpty ? "There are no projects that have started yet." : undefined}
          tooltipValueFormat={(points) => {
            if (points.isEmpty) return "";
            // nivo 0.99 types `x` as possibly missing, for a gap in the line. This series never has one, but
            // there is no date to name in the sentence if it ever does.
            const startedBefore = points.first!.data.x;
            if (!startedBefore) return "";
            const formattedDate = new Date(startedBefore).toLocaleDateString(undefined, {
              month: "long",
              year: "numeric",
            });
            return `Projects started before ${formattedDate} had an average of ${points.first!.data.y}% committed at start`;
          }}
          margin={{ top: 5, right: 5, bottom: 22, left: 35 }}
          axisLeft={{
            tickValues: yTicks,
            tickPadding: 10,
            tickSize: 0,
            format: (value: number) => `${Math.round(value)}%`,
          }}
          axisBottom={{
            tickSize: 0,
            tickPadding: 10,
            tickValues: xTicks,
            // Add day information to the tick for smaller timeframes
            format: chartData.nonEmpty && dateRangeInMonths < 6 ? "%b%d" : "%b",
          }}
          enableSlices="x"
          yScale={{ type: "linear", min: "auto", max: "auto" }}
          gridYValues={yTicks}
          xScale={{ type: "time", format: "%Y-%m-%d", precision: "day" }}
          xFormat="time:%b %Y"
        />
      </div>
    </div>
  );
}

export function mapToLineChartData(data: BudgetOverviewTab_SellableProjectFragment[]) {
  // Group/sort by date and calculate cumulative average
  const grouped = Object.values(
    data
      .filter((p) => !!p.readyToStartDate)
      .reduce(
        (acc, p) => {
          const date = p.readyToStartDate!.toString();
          const value = p.committedCostCodesAtStartInBasisPoints ?? 0;
          if (!acc[date]) acc[date] = { values: [], date };
          acc[date].values.push(value);
          return acc;
        },
        {} as Record<string, { values: number[]; date: string }>,
      ),
  ).sort((a, b) => Temporal.PlainDate.compare(a.date, b.date));
  if (grouped.isEmpty) return [];

  const cumulativeValues: number[] = [];
  const points = grouped.map((g) => {
    cumulativeValues.push(...g.values);
    const average = cumulativeValues.sum() / cumulativeValues.length;
    return { x: g.date, y: average / 100 };
  });

  return [{ id: "Average Committed at Start", data: points }];
}

function getChartTicks(data: { x: string; y: number }[]): { xTicks: Date[] | undefined; yTicks: number[] } {
  return data.isEmpty
    ? { xTicks: undefined, yTicks: [73, 82, 91, 100] }
    : {
        xTicks: getDateTicks(
          data.map((d) => new Date(d.x)),
          6,
          true,
        ),
        yTicks: getNumberTicks(
          data.map((d) => d.y),
          4,
        ),
      };
}

function calendarMonthDiff(start: string, end: string): number {
  const startDate = Temporal.PlainDate.from(start);
  const endDate = Temporal.PlainDate.from(end);
  return (endDate.year - startDate.year) * 12 + (endDate.month - startDate.month);
}
